import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PlugsConnected } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { deliveryAlertsChanged } from "@/components/layout/useDeliveryAlertCount";
import { PauseAlertDialog } from "./PauseAlertDialog";

const LEVEL = {
  down: { label: "Down", cls: "bg-red-700 text-white" },
  failing: { label: "Failing", cls: "bg-amber-500 text-black" },
};
const SMALL = "border px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100";

export async function resumeAlert(id) {
  try {
    const { data } = await api.post(`/delivery-alerts/${id}/resume`);
    toast.success(`Turned back on${data.released ? ` · ${data.released} held lead${data.released === 1 ? "" : "s"} going out now` : ""}`);
    deliveryAlertsChanged();
    return true;
  } catch (e) {
    toast.error(e.response?.data?.detail || "Couldn't resume it");
    return false;
  }
}

function PausedLine({ a }) {
  const p = a.pause;
  return (
    <div className="w-full text-[11px] text-zinc-700" data-testid={`delivery-alert-paused-${a.id}`}>
      <b>Paused</b> {p.scope === "buyer" ? "(whole buyer)" : "(this delivery place)"} by {p.by || "staff"} · {p.held} held
      {p.rerouted ? ` · ${p.rerouted} sent to the next buyer` : ""} ·{" "}
      {a.status === "resolved" ? "a test send worked — ready to resume" : p.auto_resume ? "turns back on when a test send works" : "resume it when it's fixed"}
    </div>
  );
}

function AlertActions({ a, canAct, onDismiss, onPause, onResume }) {
  const paused = a.pause?.active;
  return (
    <span className="ml-auto flex flex-wrap items-center gap-2">
      {a.client_id && (
        <Link to={`/clients/${a.client_id}/manage`} data-testid={`delivery-alert-open-${a.id}`}
          className={`${SMALL} border-black bg-white hover:bg-black hover:text-white`}>Open buyer to test</Link>
      )}
      {canAct && paused && (
        <button type="button" onClick={() => onResume(a.id)} data-testid={`delivery-alert-resume-${a.id}`}
          className={`${SMALL} border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800`}>Resume</button>
      )}
      {canAct && !paused && a.level === "down" && (
        <button type="button" onClick={() => onPause(a)} data-testid={`delivery-alert-pause-${a.id}`}
          className={`${SMALL} border-red-700 bg-red-700 text-white hover:bg-red-800`}>Pause</button>
      )}
      {canAct && !paused && (
        <button type="button" onClick={() => onDismiss(a.id)} data-testid={`delivery-alert-dismiss-${a.id}`}
          className={`${SMALL} border-zinc-400 text-zinc-600 hover:bg-zinc-800 hover:text-white`}>Hide until it works</button>
      )}
    </span>
  );
}

function AlertRow({ a, ...actions }) {
  const lv = LEVEL[a.level] || LEVEL.failing;
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs" data-testid={`delivery-alert-${a.id}`}>
      <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${lv.cls}`} data-testid={`delivery-alert-level-${a.id}`}>{lv.label}</span>
      <span className="font-bold">{a.destination_name}</span>
      {a.client_name && <span className="text-zinc-600">{a.client_name}</span>}
      <span className="text-red-800">{a.streak} failed {a.streak === 1 ? "send" : "sends in a row"} · last response {a.last_code}</span>
      <span className="text-zinc-500">{a.last_source === "test" ? "test send" : "lead delivery"} · {fmtDate(a.last_failed_at)}</span>
      <AlertActions a={a} {...actions} />
      {a.pause?.active && <PausedLine a={a} />}
      {a.last_error && <div className="w-full truncate font-mono text-[10px] text-zinc-500">{a.last_error}</div>}
    </li>
  );
}

// Delivery places whose sends are failing right now (any failure = Failing, 3 in a row = Down), plus paused ones.
export function DeliveryAlertsCard({ showClear = false }) {
  const [data, setData] = useState(null);
  const [pausing, setPausing] = useState(null);
  const load = () => api.get("/delivery-alerts").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);
  const refresh = () => { deliveryAlertsChanged(); load(); };
  const dismiss = async (id) => {
    try {
      await api.post(`/delivery-alerts/${id}/dismiss`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't hide the alert"); }
  };
  const resume = async (id) => { if (await resumeAlert(id)) load(); };
  if (!data) return null;
  if (!data.alerts.length) {
    return showClear ? <div className="mb-5 border-2 border-emerald-700 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-900" data-testid="delivery-alerts-clear">Every delivery place is working — no failed sends waiting.</div> : null;
  }
  const down = data.alerts.filter((a) => a.level === "down" && a.status === "open").length;
  return (
    <section className="mb-5 border-2 border-red-700 bg-white" data-testid="delivery-alerts-card">
      <div className="flex flex-wrap items-center gap-2 border-b-2 border-red-700 bg-red-50 px-3 py-2">
        <PlugsConnected size={18} weight="fill" className="text-red-700" />
        <div className="text-sm font-bold text-red-900" data-testid="delivery-alerts-title">
          {down ? `${down} delivery place${down === 1 ? " is" : "s are"} down` : "Deliveries need a look"} · {data.alerts.length} alert{data.alerts.length === 1 ? "" : "s"}
        </div>
      </div>
      <p className="px-3 pt-2 text-xs text-zinc-600">Any failed send shows as <b>Failing</b>; {data.down_after} in a row is <b>Down</b> and can be paused so new leads go to the next matching buyer. Each alert clears when the next send works.</p>
      <ul className="divide-y divide-zinc-200" data-testid="delivery-alerts-list">
        {data.alerts.map((a) => (
          <AlertRow key={a.id} a={a} canAct={data.can_dismiss} onDismiss={dismiss} onPause={setPausing} onResume={resume} />
        ))}
      </ul>
      {pausing && (
        <PauseAlertDialog alert={pausing} open={Boolean(pausing)} onOpenChange={(v) => { if (!v) setPausing(null); }} onDone={refresh} />
      )}
    </section>
  );
}
